import React, { useState, useEffect } from 'react'
import { Button, Card, DatePicker, Form, Icon, Input, InputNumber, message, Modal, Select, Spin, Tag, Upload } from 'antd'
import moment from 'moment'
import AssetSearch from '../../components/AssetSearch'
import Mileage from '../Fuel/Mileage'
import MasterModal from '../../components/MasterModal'
import LocationPickerModal from '../../components/LocationPickerModal'
import TripSelectModal from './TripSelectModal'
import axios from '../../util/Api'
import { sanitizeFieldName } from '../../util/methods'

const { Option } = Select
const { TextArea } = Input

const FLEET_START = 'FLEET_TRIP_START'
const FLEET_END = 'FLEET_TRIP_END'
const sameSecond = (a, b) => Math.floor(a / 1000) === Math.floor(b / 1000)

// Stable reference so the reused Mileage component's `[meterReadings]` effect doesn't
// re-fire on every render (a fresh [] literal each render causes an infinite update loop).
const EMPTY_READINGS = []

function Create (props) {
  const { form, match, fetch, data, title, resource, loading, filters, onSubmit, assetData, assetSearchLoading, searchGlobalAssetData, authUser } = props
  const { getFieldDecorator, validateFields } = form
  const isEdit = title === 'EDIT'

  const [asset, setAsset] = useState(null)
  // Mirror the form field's initialValue so Mileage has a date immediately (before any manual change).
  const [selectedDate, setSelectedDate] = useState(moment())
  const [documents, setDocuments] = useState([])
  const [existingDocuments, setExistingDocuments] = useState([])
  const [sources, setSources] = useState([])
  const [selectedSource, setSelectedSource] = useState(undefined)
  const [meterReadings, setMeterReadings] = useState([])
  const [currentAssetMeters, setCurrentAssetMeters] = useState([])
  // Fleet trip linkage (FLEET_TRIP_START / FLEET_TRIP_END sources).
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [tripSelectVisible, setTripSelectVisible] = useState(false)
  const [fleetTripLocation, setFleetTripLocation] = useState(null)
  const [fleetTripRemark, setFleetTripRemark] = useState('')
  const [tripLocPickerVisible, setTripLocPickerVisible] = useState(false)

  // Source options (which sources are valid for a fuel level reading, and which are dual-linked)
  // come straight from the backend so the two stay in sync.
  useEffect(() => {
    axios.get('/fuel-level-reading/sources')
      .then(res => setSources(res.data || []))
      .catch(() => setSources([]))
  }, [])

  // Load the record being edited.
  useEffect(() => {
    if (isEdit && match && match.params && match.params.id && fetch) {
      fetch(match.params.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prefill from the loaded record (edit mode).
  useEffect(() => {
    if (isEdit && data && data._id) {
      const assetId = (data.asset && data.asset._id) ? data.asset._id : data.asset
      setAsset(assetId)
      setSelectedSource(data.source)
      setSelectedDate(data.readingDate ? moment(data.readingDate) : null)
      setExistingDocuments(data.documents || [])
      form.setFieldsValue({
        source: data.source,
        asset: assetId,
        readingDate: data.readingDate ? moment(data.readingDate) : undefined,
        fuelLevel: data.fuelLevel,
        fuelReadingMethod: (data.fuelReadingMethod && data.fuelReadingMethod._id) || data.fuelReadingMethod
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data._id])

  // Fuel reading methods come from the schema filters (populated as a `master` ref), so the list
  // refreshes automatically after creating one via the NEW button. Only active methods are usable.
  const methodFilter = (filters && filters['fuelReadingMethod']) || {}
  const fuelReadingMethods = ((methodFilter.data && methodFilter.data.data) || []).filter(m => m.isActive !== false)
  const showMethodNewButton = !!(methodFilter.options && methodFilter.options.master)

  const selectedSourceOption = sources.find(s => s.value === selectedSource) || null
  const requiresMeterReading = !!(selectedSourceOption && selectedSourceOption.requiresMeterReading)

  const isFleetSource = selectedSource === FLEET_START || selectedSource === FLEET_END
  const fleetMode = selectedSource === FLEET_END ? 'end' : 'start'
  // Whether creating this reading will also mutate the trip's status (start/end it).
  const fleetAction = (() => {
    if (!selectedTrip) return null
    if (selectedSource === FLEET_START && selectedTrip.tripStatus === 'PLANNED') return 'start'
    if (selectedSource === FLEET_END && selectedTrip.tripStatus === 'STARTED') return 'end'
    return null
  })()

  const handleAssetSelect = (value) => {
    setAsset(value)
    setSelectedTrip(null) // trips are asset-specific
    // Keep the form field in sync: selecting a pre-loaded option (without searching) otherwise
    // leaves the 'asset' field empty and trips the required-rule on submit.
    form.setFieldsValue({ asset: value })
  }

  const handleSourceChange = (value) => {
    setSelectedSource(value)
    setSelectedTrip(null)
    setFleetTripLocation(null)
    setFleetTripRemark('')
  }

  const handleSelectTrip = (trip) => {
    setTripSelectVisible(false)
    const readingDate = form.getFieldValue('readingDate')
    // For an already started/completed trip the reading time must match the trip's actual time.
    const checkSync = (actualRaw, label) => {
      const actual = actualRaw ? moment(actualRaw) : null
      if (actual && (!readingDate || !sameSecond(readingDate.valueOf(), actual.valueOf()))) {
        Modal.confirm({
          title: 'Time mismatch',
          content: `This trip's actual ${label} time is ${actual.format('DD/MM/YYYY HH:mm:ss')}, which differs from the Reading Date & Time. Use the trip's actual ${label} time as the Reading Date & Time?`,
          okText: 'Use trip time',
          cancelText: 'Keep my time',
          onOk: () => {
            form.setFieldsValue({ readingDate: actual })
            setSelectedDate(actual)
          }
        })
      }
    }
    if (selectedSource === FLEET_START && trip.tripStatus === 'STARTED') {
      checkSync(trip.actualStartDateTime, 'start')
    } else if (selectedSource === FLEET_END && trip.tripStatus === 'COMPLETED') {
      checkSync(trip.actualEndDateTime, 'end')
    }
    setSelectedTrip(trip)
  }

  const handleMeterReadingChange = (type, value) => {
    if (type === 'meterReading' && value != null) {
      setMeterReadings(Array.isArray(value) ? value : [value])
    }
  }

  const removeExistingDocument = (doc, idx) => {
    setExistingDocuments(prev => prev.filter((d, i) => (d.url || i) !== (doc.url || idx)))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    validateFields((err, values) => {
      if (err) {
        return
      }

      // Edit: only fuel level, method and attachments are editable.
      if (isEdit) {
        onSubmit({
          fuelLevel: Number(values.fuelLevel),
          fuelReadingMethod: values.fuelReadingMethod,
          documents,
          existingDocuments
        }, match.params.id)
        return
      }

      if (!asset) {
        message.error('Asset is required')
        return
      }
      if (!selectedSource) {
        message.error('Source is required')
        return
      }

      const payload = {
        asset,
        source: selectedSource,
        fuelReadingMethod: values.fuelReadingMethod,
        fuelLevel: Number(values.fuelLevel),
        readingDate: values.readingDate,
        documents
      }

      if (requiresMeterReading) {
        const meterMap = (currentAssetMeters || []).reduce((acc, meter) => {
          if (meter && meter._id) acc[meter._id.toString()] = meter
          return acc
        }, {})
        const readings = (meterReadings || [])
          .filter((item) => item && item.meter)
          .filter((item) => item.isFaultyMeter || (item.reading !== '' && item.reading !== null && item.reading !== undefined))
          .map((item) => ({
            meter: item.meter && item.meter._id ? item.meter._id : item.meter,
            reading: item.isFaultyMeter ? 0 : Number(item.reading),
            isFaultyMeter: !!item.isFaultyMeter
          }))

        const expectedMeters = (currentAssetMeters || []).filter(m => m.asset && m.asset._id && asset && m.asset._id.toString() === asset.toString())
        const requiredMeterIds = expectedMeters.map(m => m && m._id && m._id.toString()).filter(Boolean)
        const providedNonFaulty = readings.filter(r => !r.isFaultyMeter).map(r => r.meter.toString())
        const providedAny = readings.map(r => r.meter.toString())

        if (!readings.length) {
          message.error('Meter readings are required for the selected source')
          return
        }
        const missing = requiredMeterIds.filter(id => !providedAny.includes(id))
        if (missing.length > 0) {
          const missingNames = expectedMeters
            .filter(m => m && m._id && missing.includes(m._id.toString()))
            .map(m => m.meterNo || m._id)
            .join(', ')
          message.error(`Missing readings for meter(s): ${missingNames}`)
          return
        }
        const invalid = readings.filter(r => !r.isFaultyMeter && !(Number(r.reading) > 0))
        if (invalid.length > 0) {
          const invalidNames = invalid.map(r => (meterMap[r.meter.toString()] && meterMap[r.meter.toString()].meterNo) || r.meter).join(', ')
          message.error(`Meter reading must be greater than 0 for meter(s): ${invalidNames}`)
          return
        }
        if (!providedNonFaulty.length && readings.length) {
          // all faulty is allowed, keep going
        }
        payload.meterReadings = readings
      }

      // Fleet trip sources must be linked to a trip; the backend will start/end it in the same transaction.
      if (isFleetSource) {
        if (!selectedTrip) {
          message.error('Please select a fleet trip for this source')
          return
        }
        payload.fleetTripId = selectedTrip._id
        if (fleetTripLocation) payload.fleetTripLocation = fleetTripLocation
        if (fleetTripRemark) payload.fleetTripRemark = fleetTripRemark
      }

      onSubmit(payload)
    })
  }

  return (
    <Spin spinning={loading}>
      <Card className='gx-card-metrics' title={`${title} ${sanitizeFieldName(resource)}`}>
        <Form onSubmit={handleSubmit} layout='vertical'>

          <Form.Item label='Reading Date & Time' style={{ width: '100%' }}>
            {getFieldDecorator('readingDate', {
              rules: [{ required: true, message: 'Reading date & time is required!' }],
              initialValue: moment()
            })(
              <DatePicker
                showTime
                format='DD/MM/YYYY HH:mm:ss'
                style={{ width: '100%' }}
                disabled={isEdit}
                disabledDate={(current) => current && current > moment().endOf('day')}
                onChange={(value) => setSelectedDate(value)}
              />
            )}
          </Form.Item>

          <Form.Item label='Asset' style={{ width: '100%' }}>
            {getFieldDecorator('asset', {
              rules: [{ required: true, message: 'Asset is required!' }]
            })(
              <AssetSearch
                styleName='gx-lt-icon-search-bar-lg'
                placeholder='Search and select an asset...'
                onChange={searchGlobalAssetData}
                onSelect={handleAssetSelect}
                assetData={assetData && assetData.filter(ast => ast.assetModel)}
                authUser={authUser}
                loading={assetSearchLoading}
                disabled={isEdit}
                defaultAsset={isEdit ? (data && data.asset) : undefined}
              />
            )}
          </Form.Item>

          <Form.Item label='Source' style={{ width: '100%' }}>
            {getFieldDecorator('source', {
              rules: [{ required: true, message: 'Source is required!' }]
            })(
              <Select
                showSearch
                placeholder='Select a source'
                style={{ width: '100%' }}
                optionFilterProp='children'
                disabled={isEdit}
                onChange={handleSourceChange}
              >
                {sources.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}{s.requiresMeterReading ? ' (needs meter reading)' : ''}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          {!isEdit && isFleetSource && (
            <Form.Item label={selectedSource === FLEET_END ? 'Fleet Trip (to end)' : 'Fleet Trip (to start)'} style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button icon='car' disabled={!asset} onClick={() => setTripSelectVisible(true)}>
                  {selectedTrip ? 'Change Trip' : 'Select Trip'}
                </Button>
                {!asset && <span style={{ color: '#8c8c8c' }}>Select an asset first</span>}
                {selectedTrip && (
                  <span>
                    <strong>{selectedTrip.tripNo}</strong> — {selectedTrip.tripName}{' '}
                    <Tag color='blue'>{selectedTrip.tripStatus}</Tag>
                  </span>
                )}
              </div>
              {fleetAction && (
                <div style={{ marginTop: 10, padding: 10, border: '1px dashed #91d5ff', borderRadius: 6, background: '#f0faff' }}>
                  <div style={{ marginBottom: 6, color: '#1890ff' }}>
                    <Icon type='info-circle' /> Creating this reading will {fleetAction === 'end' ? 'END' : 'START'} the trip. Actual {fleetAction === 'end' ? 'end' : 'start'} time = the Reading Date &amp; Time above.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Button size='small' icon='environment' onClick={() => setTripLocPickerVisible(true)}>
                      {fleetTripLocation ? 'Change actual location' : 'Set actual location (optional)'}
                    </Button>
                    {fleetTripLocation && (
                      <span style={{ color: '#595959' }}>
                        {fleetTripLocation.latitude}, {fleetTripLocation.longitude}
                        <a style={{ marginLeft: 8, color: '#f5222d' }} onClick={() => setFleetTripLocation(null)}>Clear</a>
                      </span>
                    )}
                  </div>
                  <TextArea
                    rows={2}
                    placeholder={`Remark for trip ${fleetAction === 'end' ? 'end' : 'start'} (optional)`}
                    value={fleetTripRemark}
                    onChange={(e) => setFleetTripRemark(e.target.value)}
                  />
                </div>
              )}
            </Form.Item>
          )}

          <Form.Item label='Fuel Reading Method' style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {getFieldDecorator('fuelReadingMethod', {
                  rules: [{ required: true, message: 'Fuel reading method is required!' }]
                })(
                  <Select
                    showSearch
                    placeholder='Select fuel reading method'
                    style={{ width: '100%' }}
                    optionFilterProp='children'
                    notFoundContent={<span>No active methods — use NEW to add one</span>}
                  >
                    {fuelReadingMethods.map((m) => (
                      <Option key={m._id} value={m._id}>{m.name}</Option>
                    ))}
                  </Select>
                )}
              </div>
              {showMethodNewButton && (
                <MasterModal resource={resource} item={methodFilter.options.ref} title={'CREATE'} />
              )}
            </div>
          </Form.Item>

          <Form.Item label='Fuel Level' style={{ width: '100%' }}>
            {getFieldDecorator('fuelLevel', {
              rules: [
                { required: true, message: 'Fuel level is required!' },
                {
                  validator: (_, value, callback) => {
                    if (value === undefined || value === null || value === '') {
                      callback('Fuel level is required!')
                      return
                    }
                    if (Number(value) < 0) {
                      callback('Fuel level cannot be negative')
                      return
                    }
                    callback()
                  }
                }
              ]
            })(
              <InputNumber min={0} style={{ width: 240 }} placeholder='Enter fuel level' />
            )}
          </Form.Item>

          {!isEdit && requiresMeterReading && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6, color: '#1890ff', fontWeight: 500 }}>
                <Icon type='info-circle' /> This source also records meter readings for the asset.
              </div>
              <Mileage
                key='mileage'
                visible={!!asset}
                form={form}
                meterUnits={{}}
                selectedAsset={asset}
                selectedDate={selectedDate}
                isDateBased
                meterReadings={EMPTY_READINGS}
                handleChange={handleMeterReadingChange}
                setCurrentAssetMeters={setCurrentAssetMeters}
              />
              {!asset && (
                <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>Select an asset to load its meters.</div>
              )}
            </div>
          )}

          <Form.Item label='Attachments (optional)' style={{ width: '100%' }}>
            {isEdit && existingDocuments.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Existing attachments:</div>
                {existingDocuments.map((doc, idx) => (
                  <div key={doc.url || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <a href={doc.url} target='_blank' rel='noopener noreferrer'>{doc.name || doc.url}</a>
                    <a onClick={() => removeExistingDocument(doc, idx)} style={{ color: '#f5222d' }}>Remove</a>
                  </div>
                ))}
              </div>
            )}
            <Upload
              multiple
              fileList={documents}
              beforeUpload={(file) => {
                setDocuments(prev => [...prev, file])
                return false
              }}
              onRemove={(file) => {
                setDocuments(prev => prev.filter(f => f.uid !== file.uid))
              }}
            >
              <Button>
                <Icon type='upload' /> Select Files
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Button type='primary' className='gx-mb-0' htmlType='submit' loading={loading}>
              {isEdit ? 'UPDATE' : 'CREATE'}
            </Button>
          </Form.Item>
        </Form>

        <TripSelectModal
          visible={tripSelectVisible}
          mode={fleetMode}
          assetId={asset}
          onSelect={handleSelectTrip}
          onCancel={() => setTripSelectVisible(false)}
        />
        <LocationPickerModal
          visible={tripLocPickerVisible}
          initialLat={fleetTripLocation && fleetTripLocation.latitude}
          initialLng={fleetTripLocation && fleetTripLocation.longitude}
          initialAddress={fleetTripLocation && fleetTripLocation.address}
          onOk={(loc) => { setFleetTripLocation(loc); setTripLocPickerVisible(false) }}
          onCancel={() => setTripLocPickerVisible(false)}
        />
      </Card>
    </Spin>
  )
}

const CreateForm = Form.create({
  name: 'fuel_level_reading',
  onFieldsChange (props, changedFields) {
    if (props.onChange) props.onChange(changedFields)
  }
})(Create)

export default CreateForm
